export interface DateFormatOptions {
  removeDate?: boolean;
  removeYear?: boolean;
  removeTime?: boolean;
  removeSeconds?: boolean;
}

export const formatDateTime = (
  dateTime: Date,
  dateFormatOptions?: DateFormatOptions
): string => {
  if (Number.isNaN(dateTime.getDate())) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat('sr-RS', {
    day: dateFormatOptions?.removeDate ? undefined : '2-digit',
    month: dateFormatOptions?.removeDate ? undefined : '2-digit',
    year:
      dateFormatOptions?.removeDate || dateFormatOptions?.removeYear
        ? undefined
        : 'numeric',
    hour: dateFormatOptions?.removeTime ? undefined : '2-digit',
    minute: dateFormatOptions?.removeTime ? undefined : '2-digit',
    second:
      dateFormatOptions?.removeTime || dateFormatOptions?.removeSeconds
        ? undefined
        : '2-digit',
  });

  return formatter.format(dateTime);
};

export const getMatchDateLabel = (date: Date): string => {
  if (isSameDate(date, new Date())) return 'today_label';

  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  if (isSameDate(date, tomorrowDate)) return 'tomorrow_label';

  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

export const isSameDate = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const sortArrayByDate = (
  array: any[],
  datePropertyName: string,
  direction: 'ASC' | 'DESC' = 'ASC'
) => {
  return array.sort((a, b) => {
    if (direction === 'ASC') {
      return (
        new Date(a[datePropertyName]).getDate() -
        new Date(b[datePropertyName]).getDate()
      );
    }
    return (
      new Date(b[datePropertyName]).getDate() -
      new Date(a[datePropertyName]).getDate()
    );
  });
};

/**
 * Timestamp in the one format the API accepts: "YYYY-MM-DD HH:MM:SS".
 *
 * This used to be built from a template literal split over two lines, which
 * put a newline and two spaces between the date and the time. The API rejected
 * it with 400, the caller swallowed the error, and every "today" screen came
 * up empty - since the first commit.
 */
export const prepareDateTimeForHttpRequest = (
  date: Date | null | undefined
) => {
  if (!date) return '';

  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  return `${day} ${time}`;
};

export const getEndOfDay = () => {
  const date = new Date();
  date.setHours(23, 59, 59);
  return date;
};

/** End of local calendar tomorrow (23:59:59.999), as unix seconds. */
export const getTomorrowEndUnixSec = (): number => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  t.setHours(23, 59, 59, 999);
  return Math.floor(t.getTime() / 1000);
};

/**
 * Split prematch rows by kickoff `time` (unix seconds) into local today vs tomorrow.
 * Events outside those two local days are dropped (caller should bound the fetch window).
 */
export const partitionEventsByLocalTodayTomorrow = <
  T extends { time?: number | string },
>(
  events: T[]
): { today: T[]; tomorrow: T[] } => {
  const today: T[] = [];
  const tomorrow: T[] = [];
  const now = new Date();
  const startTomorrow = new Date(now);
  startTomorrow.setDate(startTomorrow.getDate() + 1);
  startTomorrow.setHours(0, 0, 0, 0);

  for (const ev of events) {
    const ts = typeof ev.time === 'number' ? ev.time : Number(ev.time);
    if (!Number.isFinite(ts)) continue;
    const d = new Date(ts * 1000);
    if (isSameDate(d, now)) today.push(ev);
    else if (isSameDate(d, startTomorrow)) tomorrow.push(ev);
  }
  return { today, tomorrow };
};
